import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Bookmark, Share2, Check, X, Eye } from "lucide-react";
import api, { OCCASIONS, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MoireSymbol } from "@/components/MoireLogo";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import ShareCard from "@/components/ShareCard";

const MODES = [
  { id: "classic", label: "Classic", hint: "Timeless. Refined." },
  { id: "elevated", label: "Elevated", hint: "Polished, with intention." },
  { id: "statement", label: "Statement", hint: "Confident. Expressive." },
  { id: "surprise", label: "Surprise Me", hint: "MOIRÉ decides." },
];

export default function StyleMe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("occasion"); // occasion | mood | building | looks
  const [occasion, setOccasion] = useState("");
  const [mode, setMode] = useState("classic");
  const [data, setData] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [locked, setLocked] = useState({});
  const [swapFor, setSwapFor] = useState(null);
  const [alts, setAlts] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareData, setShareData] = useState(null);

  const build = async (opts = {}) => {
    setStep("building");
    const body = { occasion: occasion || null, mode: opts.mode ?? mode,
      excluded_groups: opts.excluded ?? excluded, locked: opts.locked ?? locked };
    try {
      const { data: d } = await api.post("/style-me", body);
      setData(d);
      const strong = d.looks?.[0]?.score >= 9.0;
      setTimeout(() => setStep("looks"), strong ? 1000 : 500);
    } catch { toast.error("MOIRÉ couldn't style that. Try again."); setStep("mood"); }
  };

  const openSwap = async (look, slot) => {
    setSwapFor({ slot }); setAlts("loading");
    try {
      const { data: d } = await api.post("/style-me/swap", { look_pieces: look.pieces, slot, occasion: occasion || null, mode });
      setAlts(d.alternatives);
    } catch { setAlts([]); }
  };
  const applySwap = (alt) => {
    const nl = { ...locked, [swapFor.slot]: { group: swapFor.slot, name: alt.name, color_name: alt.color_name,
      color_hex: alt.color_hex, source: alt.source, wardrobe_id: alt.wardrobe_id, image_url: alt.image_url } };
    setLocked(nl); setSwapFor(null); setAlts(null); build({ locked: nl });
  };

  const notToday = (slot) => { const ex = [...excluded, slot]; setExcluded(ex); build({ excluded: ex }); };
  const shift = (newMode) => { setMode(newMode); build({ mode: newMode }); };

  const save = async (look) => {
    if (!user) { toast("Sign in to save looks."); navigate("/signin"); return; }
    try { await api.post("/style-me/save", { occasion, mode, pieces: look.pieces, score: look.score, take: look.take }); toast.success("Look saved."); }
    catch { toast.error("Couldn't save."); }
  };
  const wear = async (look) => {
    if (!user) { toast("Sign in to wear looks."); return; }
    try { await api.post("/style-me/wear", { occasion, mode, pieces: look.pieces, score: look.score }); toast.success("Noted — enjoy the look."); }
    catch {}
  };
  const share = (look) => {
    const cols = look.pieces.slice(0, 3).map(p => p.color_name).join("  ");
    setShareData({ baseColor: look.pieces[0]?.color_name, baseHex: look.pieces[0]?.color_hex,
      matchColor: look.pieces[1]?.color_name || "", matchHex: look.pieces[1]?.color_hex || "#000",
      score: look.score, statusText: "Tonight's look" });
    setShareOpen(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20 min-h-[70vh]">
      {step === "occasion" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="flex justify-center mb-6"><MoireSymbol size={48} /></div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Style Me</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">Where are we going?</h1>
          <p className="font-sans text-sm text-[#8A8170] mt-4 mb-10">Tell MOIRÉ where you're going. We'll take it from here.</p>
          <div className="flex flex-wrap justify-center gap-2.5 max-w-2xl mx-auto">
            {OCCASIONS.map((o) => (
              <button key={o} onClick={() => { setOccasion(o); setStep("mood"); }} data-testid={`occ-${o.replace(/\s/g,"").toLowerCase()}`}
                className="px-5 py-3 font-sans text-xs tracking-luxe uppercase border border-[#F5F2EA]/15 text-[#F5F2EA]/80 hover:border-[#C8A97E] hover:text-[#F5F2EA] transition-colors">{o}</button>
            ))}
          </div>
          <button onClick={() => { setOccasion(""); setStep("mood"); }} data-testid="just-style-me"
            className="mt-8 font-sans text-xs tracking-luxe uppercase text-[#C8A97E] link-underline">Just Style Me</button>
        </motion.div>
      )}

      {step === "mood" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">{occasion || "Any occasion"}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">What's the mood?</h1>
          <div className="grid sm:grid-cols-2 gap-4 mt-10 max-w-xl mx-auto">
            {MODES.map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
                className={`p-6 border text-left transition-colors ${mode === m.id ? "border-[#C8A97E] bg-[#111111]" : "border-[#F5F2EA]/15 hover:border-[#C8A97E]/60"}`}>
                <div className="font-serif text-2xl text-[#F5F2EA]">{m.label}</div>
                <div className="font-sans text-xs text-[#8A8170] mt-1">{m.hint}</div>
              </button>
            ))}
          </div>
          <button onClick={() => { setLocked({}); setExcluded([]); build(); }} data-testid="style-me-go"
            className="mt-10 inline-flex items-center gap-3 bg-[#C8A97E] text-[#0D0D0D] px-10 py-4 font-sans text-sm tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
            <Sparkles size={16} strokeWidth={1.5} /> Style Me</button>
          <div><button onClick={() => setStep("occasion")} className="mt-5 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E]">Back</button></div>
        </motion.div>
      )}

      {step === "building" && <AnalyzingSequence stages={["Looking through your wardrobe…", "Finding the strongest combination…", "Finishing the look…"]} done={data?.looks?.[0]?.score >= 9.0} />}

      {step === "looks" && data && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Tonight, we're wearing…</p>
              <h1 className="font-serif text-3xl md:text-4xl text-[#F5F2EA]">{occasion || "Any occasion"} · {mode}</h1>
            </div>
            <div className="flex gap-2">
              {[["more casual", "classic"], ["bolder", "statement"], ["polished", "elevated"]].map(([lbl, m]) => (
                <button key={m} onClick={() => shift(m)} className="font-sans text-[10px] tracking-luxe uppercase border border-[#F5F2EA]/15 px-3 py-2 hover:border-[#C8A97E] transition-colors">{lbl}</button>
              ))}
            </div>
          </div>
          {!data.has_wardrobe && (
            <div className="mt-5 border border-[#C8A97E]/30 p-4 flex items-center justify-between gap-3">
              <p className="font-sans text-xs text-[#8A8170]">Add what you own and next time MOIRÉ can style the real pieces in your closet.</p>
              <button onClick={() => navigate("/wardrobe")} className="shrink-0 font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] border border-[#C8A97E]/50 px-4 py-2 hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">Start My Wardrobe</button>
            </div>
          )}

          <div className="space-y-6 mt-8">
            {data.looks.map((look, li) => (
              <div key={li} className="border border-[#F5F2EA]/10 bg-[#111111] p-6" data-testid={`look-${li}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">{look.label} · {occasion || "any"}, {mode}</p>
                    <div className="font-serif text-4xl text-[#C8A97E] mt-1">{Number(look.score).toFixed(1)}<span className="text-lg text-[#8A8170]"> / 10</span></div>
                  </div>
                  {look.score >= 9.0 && <span className="font-serif italic text-2xl text-[#C8A97E]">CLOCK IT 🤏🏼</span>}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                  {look.pieces.map((p, pi) => (
                    <div key={pi} className="border border-[#F5F2EA]/10 p-3 group relative" data-testid={`piece-${li}-${p.group}`}>
                      <div className="h-24 border border-[#F5F2EA]/10 overflow-hidden mb-3">
                        {p.image_url ? <img src={imageUrl(p.image_url)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                          : <div className="w-full h-full" style={{ backgroundColor: p.color_hex }} />}
                      </div>
                      <div className="font-serif text-lg text-[#F5F2EA] leading-tight">{p.name}</div>
                      <div className="font-sans text-[10px] tracking-wider uppercase text-[#8A8170] mt-1">
                        {p.source === "wardrobe" ? "Your wardrobe" : p.source === "start" ? "Your piece" : "MOIRÉ suggests"}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => openSwap(look, p.group)} data-testid={`swap-${li}-${p.group}`}
                          className="font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E] hover:text-[#E5C293]">Swap</button>
                        <button onClick={() => notToday(p.group)} className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA]">Not today</button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="font-serif italic text-[#F5F2EA]/85 mt-5">“{look.take}”</p>
                {look.missing_one && look.suggested[0] && (
                  <p className="font-sans text-sm text-[#C8A97E] mt-2">You're one piece away — a {look.suggested[0].name} would complete this look.</p>
                )}

                <div className="flex flex-wrap gap-2.5 mt-5">
                  <button onClick={() => { sessionStorage.setItem("moire.lookRequest", JSON.stringify({ pieces: look.pieces, occasion: occasion || null })); navigate("/look"); }} data-testid={`see-look-${li}`} className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><Eye size={14} strokeWidth={1.5} /> See The Look</button>
                  <button onClick={() => wear(look)} data-testid={`wear-${li}`} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Check size={14} strokeWidth={2} /> Wear This</button>
                  <button onClick={() => save(look)} data-testid={`save-${li}`} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Bookmark size={14} strokeWidth={1.5} /> Save Look</button>
                  <button onClick={() => share(look)} data-testid={`share-${li}`} className="flex items-center gap-2 border border-[#F5F2EA]/15 px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors"><Share2 size={14} strokeWidth={1.5} /> Share</button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-4 mt-10">
            <button onClick={() => { setLocked({}); setExcluded([]); build({ locked: {}, excluded: [] }); }} data-testid="restyle"
              className="flex items-center gap-2 border border-[#C8A97E] text-[#C8A97E] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors"><RefreshCw size={14} strokeWidth={1.5} /> Restyle</button>
            <button onClick={() => setStep("occasion")} className="font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#C8A97E]">Start over</button>
          </div>
        </motion.div>
      )}

      {swapFor && (
        <div className="fixed inset-0 z-50 bg-[#0D0D0D]/80 flex items-center justify-center p-5" onClick={() => setSwapFor(null)}>
          <div className="bg-[#0D0D0D] border border-[#C8A97E]/40 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="swap-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-[#F5F2EA]">Try instead</h3>
              <button onClick={() => setSwapFor(null)}><X size={18} className="text-[#8A8170]" /></button>
            </div>
            <div className="space-y-2 mt-4">
              {alts === "loading" && <p className="font-sans text-sm text-[#8A8170]">Finding options…</p>}
              {Array.isArray(alts) && alts.map((alt, i) => (
                <button key={i} onClick={() => applySwap(alt)} data-testid={`alt-${i}`}
                  className="w-full flex items-center gap-3 border border-[#F5F2EA]/10 p-2.5 hover:border-[#C8A97E] transition-colors text-left">
                  {alt.image_url ? <img src={imageUrl(alt.image_url)} alt={alt.name} className="h-10 w-10 object-cover border border-[#F5F2EA]/15" />
                    : <span className="h-10 w-10 border border-[#F5F2EA]/15" style={{ backgroundColor: alt.color_hex }} />}
                  <span className="font-serif text-lg text-[#F5F2EA] flex-1">{alt.name}</span>
                  <span className="font-serif text-lg text-[#C8A97E]">{Number(alt.score).toFixed(1)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ShareCard open={shareOpen} onOpenChange={setShareOpen} data={shareData} />
    </div>
  );
}
